import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-chessup-bg">
      <div className="hidden lg:block relative">
        <img src="/chessup-board.jpg" alt="Xadrez" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-chessup-deep via-chessup-deep/70 to-chessup-bg" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <Logo size={52} />
          <p className="mt-6 font-heading text-2xl text-chessup-text">Você não assiste xadrez.<br /><span className="chessup-gold-text">Você joga a aula.</span></p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-chessup-subtext hover:text-chessup-turq mb-8"><ArrowLeft className="h-4 w-4" /> Voltar ao início</Link>
          <div className="lg:hidden mb-6"><Logo size={44} /></div>
          <h1 className="font-heading text-3xl text-chessup-text">{title}</h1>
          <p className="mt-2 text-sm text-chessup-subtext">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-lg bg-chessup-deep border border-chessup-border px-4 py-3 text-chessup-text placeholder:text-chessup-subtext/60 focus:border-chessup-turq focus:outline-none transition-colors";
const btnCls = "w-full rounded-full bg-chessup-gold py-3.5 font-semibold text-chessup-deep hover:bg-chessup-goldlight transition-colors disabled:opacity-60";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      nav(u?.role === "admin" ? "/admin" : "/painel");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Entrar" subtitle="Bem-vindo de volta à ChessUp Academy.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {err && <div className="rounded-lg bg-destructive/15 border border-destructive/40 px-4 py-2.5 text-sm text-red-200" data-testid="login-error">{err}</div>}
        <input data-testid="login-email" type="email" required placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <input data-testid="login-password" type="password" required placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        <div className="text-right"><Link to="/esqueci-senha" className="text-sm text-chessup-turq hover:underline">Esqueci minha senha</Link></div>
        <button disabled={loading} className={btnCls} data-testid="login-submit">{loading ? "Entrando..." : "Entrar"}</button>
      </form>
      <p className="mt-6 text-center text-sm text-chessup-subtext">Não tem conta? <Link to="/criar-conta" className="text-chessup-turq hover:underline">Criar conta</Link></p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await register(name, email, password); nav("/onboarding"); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Criar conta" subtitle="Comece sua jornada no xadrez agora.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {err && <div className="rounded-lg bg-destructive/15 border border-destructive/40 px-4 py-2.5 text-sm text-red-200" data-testid="register-error">{err}</div>}
        <input data-testid="register-name" required placeholder="Seu nome" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        <input data-testid="register-email" type="email" required placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <input data-testid="register-password" type="password" required minLength={6} placeholder="Senha (mín. 6 caracteres)" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        <button disabled={loading} className={btnCls} data-testid="register-submit">{loading ? "Criando..." : "COMEÇAR AGORA"}</button>
      </form>
      <p className="mt-6 text-center text-sm text-chessup-subtext">Já tem conta? <Link to="/entrar" className="text-chessup-turq hover:underline">Entrar</Link></p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""); const [sent, setSent] = useState(false); const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    const api = (await import("@/lib/api")).default;
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Recuperar senha" subtitle="Enviaremos instruções para o seu e-mail.">
      {sent ? (
        <div className="rounded-lg bg-chessup-turq/10 border border-chessup-turq/40 px-4 py-4 text-sm text-chessup-text" data-testid="forgot-sent">
          Se o e-mail existir, enviamos um link de recuperação. Verifique sua caixa de entrada.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          {err && <div className="rounded-lg bg-destructive/15 border border-destructive/40 px-4 py-2.5 text-sm text-red-200" data-testid="forgot-error">{err}</div>}
          <input data-testid="forgot-email" type="email" required placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          <button disabled={loading} className={btnCls} data-testid="forgot-submit">{loading ? "Enviando..." : "Enviar link"}</button>
        </form>
      )}
      <p className="mt-6 text-center text-sm text-chessup-subtext"><Link to="/entrar" className="text-chessup-turq hover:underline">Voltar para o login</Link></p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const [password, setPassword] = useState(""); const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    const api = (await import("@/lib/api")).default;
    try { await api.post("/auth/reset-password", { token, password }); nav("/entrar"); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Nova senha" subtitle="Defina uma nova senha para sua conta.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        {err && <div className="rounded-lg bg-destructive/15 border border-destructive/40 px-4 py-2.5 text-sm text-red-200">{err}</div>}
        <input type="password" required minLength={6} placeholder="Nova senha" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        <button disabled={loading} className={btnCls}>{loading ? "Salvando..." : "Salvar nova senha"}</button>
      </form>
    </AuthShell>
  );
}
